import React, { useCallback } from "react";
import { CategoricalMappingValues, NumericMappingValues, VirtualConfig } from "../type";
import { Alert, Button, Form, Input, InputNumber, Row, Select, Space } from "antd";
import { ArrowRightOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useDataset } from "../Distributions/DatasetPicker";

type CategoricalVirtualMappingInputProps = {
  value?: CategoricalMappingValues[];
  referToQuestionCode: string;
  onChange?: (value: CategoricalMappingValues[]) => void;
};
const CategoricalVirtualMappingInput: React.FC<CategoricalVirtualMappingInputProps> = ({ value = [], onChange, referToQuestionCode }) => {
  const dataset = useDataset();
  const suggestedValues = Object.values(dataset?.survey.answerMaps?.[referToQuestionCode] || {});
  const updateIndex = useCallback(
    (index: number, key: keyof CategoricalMappingValues, newValue: any) => {
      const newCategories = [...value];
      newCategories[index] = { ...newCategories[index], [key]: newValue };
      onChange && onChange(newCategories);
    },
    [value, onChange],
  );
  const warnings = {
    not_in_suggested: value.flatMap((category) => dataset && category.values?.filter((v) => !suggestedValues.includes(v))).filter(Boolean),
    suggested_but_not_picked: suggestedValues.filter((v) => !value.flatMap((category) => category.values).includes(v)),
    picked_multiple_times: value.flatMap((category) => category.values)?.filter((v, i, a) => a.indexOf(v) !== i),
  };
  const warningsText = [
    warnings.not_in_suggested.length > 0 && `Values not in sample dataset: ${warnings.not_in_suggested.join(", ")}`,
    warnings.suggested_but_not_picked.length > 0 && `Suggested values not picked: ${warnings.suggested_but_not_picked.join(", ")}`,
    warnings.picked_multiple_times.length > 0 && `Values picked multiple times: ${warnings.picked_multiple_times.join(", ")}`,
  ].filter(Boolean);

  return (
    <div>
      {value.map((category, index) => (
        <Row key={index} style={{ marginBottom: 8, width: "100%" }}>
          <Space.Compact style={{ flexGrow: 1 }}>
            <Select
              mode="tags"
              style={{
                width: "100%",
                minWidth: 200,
                flexGrow: 1,
              }}
              options={suggestedValues.map((v) => ({ label: v, value: v }))}
              onChange={(v) => updateIndex(index, "values", v)}
              value={category.values}
            />
            <Input
              style={{ minWidth: 200 }}
              onChange={(e) => updateIndex(index, "name", e.target.value)}
              value={category.name}
              addonBefore={<ArrowRightOutlined />}
            />
            <Button danger icon={<DeleteOutlined />} onClick={() => onChange && onChange(value.filter((_, i) => i !== index))} />
          </Space.Compact>
        </Row>
      ))}
      <Button icon={<PlusOutlined />} onClick={() => onChange && onChange([...(value || []), { name: "", values: [] }])} />
      {warningsText.length ? (
        <Alert
          type="warning"
          message={
            <ul>
              {warningsText.map((text, i) => (
                <li key={i}>{text}</li>
              ))}
            </ul>
          }
          style={{ marginTop: 8 }}
        />
      ) : null}
    </div>
  );
};

export default CategoricalVirtualMappingInput;
